/**
 * Assistant tools a server runs rather than an editor: they read the CMS with
 * the caller's own token and touch no scene. The gateway offers them beside
 * the scene tools an editor declares (`SCENE_TOOLS`), runs them itself, and
 * serves the same ones on `/mcp`, so every host searches one catalog. They are
 * `ToolDef`s like the scene tools, and carry no prose either.
 */

import type { ToolAnnotations, ToolDef, ToolMeta, ToolOutcome } from '../editor/toolDef';
import { CmsError, cachedCatalogIndex, getAsset, searchIndex } from './catalogIndex';

/** What a server tool runs with: the caller's CMS access, and who the caller is (for the index cache). */
export interface ServerToolContext {
    readonly cms: { readonly baseUrl: string; readonly token: string };
    readonly callerId: string;
}

/** A tool a server runs (`meta.runsOn: 'server'`), never an editor: hosts that offer tools to a model offer it from there. */
export type ServerTool = ToolDef<ServerToolContext>;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_RESULTS = 50;

/** Both tools only read the CMS. */
const READS: ToolAnnotations = { readOnlyHint: true, destructiveHint: false, idempotentHint: true };
const reads = (verb: string): ToolMeta => ({ runsOn: 'server', edits: false, serial: false, requires: [], verb });

const fail = (text: string): ToolOutcome => ({ text, isError: true });
const fields = (input: unknown): Record<string, unknown> =>
    input && typeof input === 'object' && !Array.isArray(input) ? input as Record<string, unknown> : {};

/** A CMS failure as the model reads it: a 401/403 is the caller's token, not the catalog. */
function cmsFailure(error: unknown): ToolOutcome {
    if (error instanceof CmsError && (error.status === 401 || error.status === 403)) {
        return fail(`The CMS refused the caller's token (${error.status}).`);
    }
    return fail(`CMS request failed: ${error instanceof Error ? error.message : String(error)}`);
}

const searchCatalogTool: ServerTool = {
    name: 'search_catalog',
    annotations: READS,
    meta: reads('Searching the catalog'),
    inputSchema: {
        type: 'object',
        properties: { query: { type: 'string' }, team_id: { type: 'string' }, limit: { type: 'number' } },
        required: ['query'],
    },
    async run(input, { cms, callerId }) {
        const { query, team_id: rawTeamId, limit } = fields(input);
        // An empty team id means every team, as an omitted one does.
        const teamId = rawTeamId === '' ? undefined : rawTeamId;
        if (typeof query !== 'string') return fail('query is the words to search for.');
        // A team id becomes a CMS path, so anything but a GUID is refused before it can change which path is read.
        if (teamId !== undefined && (typeof teamId !== 'string' || !GUID.test(teamId))) {
            return fail('team_id must be a Trace team id (a GUID). Omit it to search all of the caller\'s teams.');
        }
        let count = 10;
        if (limit !== undefined) {
            if (typeof limit !== 'number' || !Number.isInteger(limit) || limit < 1 || limit > MAX_RESULTS) return fail(`limit is a whole number from 1 to ${MAX_RESULTS}.`);
            count = limit;
        }
        try {
            const index = await cachedCatalogIndex(cms.baseUrl, cms.token, callerId, teamId);
            const results = searchIndex(index, query, count).map(a => ({
                id: a.id, title: a.title, entityType: a.entityType, source: a.source, sourceType: a.sourceType, url: a.url, thumbnailUrl: a.thumbnailUrl,
            }));
            return { text: JSON.stringify({ results, indexed: index.length }, null, 2) };
        } catch (error) {
            return cmsFailure(error);
        }
    },
};

const getAssetTool: ServerTool = {
    name: 'get_asset',
    annotations: READS,
    meta: reads('Reading an asset'),
    inputSchema: { type: 'object', properties: { asset_id: { type: 'string' } }, required: ['asset_id'] },
    async run(input, { cms }) {
        const { asset_id: assetId } = fields(input);
        // The id becomes a CMS path too.
        if (typeof assetId !== 'string' || !GUID.test(assetId)) return fail('asset_id is an asset id from search_catalog (a GUID).');
        try {
            return { text: JSON.stringify(await getAsset(cms.baseUrl, cms.token, assetId), null, 2) };
        } catch (error) {
            return cmsFailure(error);
        }
    },
};

export const SERVER_TOOLS: readonly ServerTool[] = [searchCatalogTool, getAssetTool];

/** The server tool named `name`, or undefined. */
export function serverTool(name: string): ServerTool | undefined {
    return SERVER_TOOLS.find(tool => tool.name === name);
}
